import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import type { Agent, AgentCategory } from '@/types'
import { AgentCard } from './AgentCard'

const dotColors: Record<AgentCategory, string> = {
  System: 'bg-info-foreground',
  Custom: 'bg-success-foreground',
}

interface AgentCategorySectionProps {
  projectId: string
  category: AgentCategory
  agents: Agent[]
  isExpanded: boolean
  onToggle: () => void
  emptyText: string
}

export function AgentCategorySection({
  projectId,
  category,
  agents,
  isExpanded,
  onToggle,
  emptyText,
}: AgentCategorySectionProps) {
  const bodyId = `agents-section-body-${category}`

  return (
    <section data-testid={`agents-section-${category}`}>
      <button
        type="button"
        onClick={onToggle}
        className="flex items-center gap-3 rounded-lg px-3 py-2.5 text-left transition-colors hover:bg-muted"
        aria-expanded={isExpanded}
        aria-controls={bodyId}
        data-testid={`agents-section-header-${category}`}
      >
        <ChevronDown
          className={cn('h-4 w-4 shrink-0 text-foreground transition-transform duration-200', isExpanded ? 'rotate-0' : '-rotate-90')}
        />
        <span className={cn('h-2.5 w-2.5 shrink-0 rounded-full', dotColors[category])} />
        <span className="text-sm font-bold leading-[1.6] tracking-[0.015em] text-foreground">{category}</span>
        <span className="inline-flex h-4 min-w-4 shrink-0 items-center justify-center rounded-full bg-button px-1 text-[10px] font-semibold text-button-foreground">
          {agents.length}
        </span>
      </button>

      {isExpanded && (
        <div id={bodyId} className="mt-2">
          {agents.length === 0 ? (
            <div
              className="rounded-2xl border border-dashed border-border bg-table-row p-8 text-center text-sm text-muted-foreground"
              data-testid={`agents-section-empty-${category}`}
            >
              {emptyText}
            </div>
          ) : (
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              {agents.map(agent => <AgentCard key={agent.id} projectId={projectId} agent={agent} />)}
            </div>
          )}
        </div>
      )}
    </section>
  )
}
